import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import FilterBar, { type FilterGroup } from "@/components/feature/FilterBar";
import Pagination from "@/components/feature/Pagination";
import MovieCard from "@/components/feature/MovieCard";
import { movies, filterGenres, filterRegions, filterYears, filterCategories } from "@/mocks/movies";

const PAGE_SIZE = 8;

export default function Category() {
  const [params, setParams] = useSearchParams();

  const category = params.get("category") || "全部";
  const genre = params.get("genre") || "全部";
  const region = params.get("region") || "全部";
  const year = params.get("year") || "全部";
  const sort = params.get("sort") || "update";
  const page = Number(params.get("page") || "1");

  const groups: FilterGroup[] = [
    { key: "category", label: "内容分类", options: filterCategories },
    { key: "genre", label: "影视类型", options: filterGenres },
    { key: "region", label: "地区", options: filterRegions },
    { key: "year", label: "年份", options: filterYears },
  ];

  const values = { category, genre, region, year };

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value === "全部") next.delete(key);
    else next.set(key, value);
    if (key !== "page") next.delete("page");
    setParams(next, { replace: true });
  };

  const reset = () => {
    const next = new URLSearchParams();
    if (sort !== "update") next.set("sort", sort);
    setParams(next, { replace: true });
  };

  const filtered = useMemo(() => {
    const list = movies.filter(
      (m) =>
        (category === "全部" || m.category === category) &&
        (genre === "全部" || m.genre === genre) &&
        (region === "全部" || m.region === region) &&
        (year === "全部" || String(m.year) === year),
    );
    const sorted = [...list];
    if (sort === "rating") sorted.sort((a, b) => b.rating - a.rating);
    else sorted.sort((a, b) => b.year - a.year);
    return sorted;
  }, [category, genre, region, year, sort]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE) || 1;
  const safePage = Math.min(page, totalPages);
  const pageItems = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const activeTags = [
    category !== "全部" ? category : null,
    genre !== "全部" ? genre : null,
    region !== "全部" ? region : null,
    year !== "全部" ? `${year}` : null,
  ].filter(Boolean) as string[];

  return (
    <div className="w-full">
      <div className="bg-primary-950">
        <div className="w-full px-4 md:px-8 pt-6 pb-10">
          <Breadcrumb items={[{ label: "影视分类" }]} />
          <h1 className="font-heading text-2xl md:text-4xl font-bold text-background-50">
            {category === "全部" ? "影视作品大全" : `${category}作品大全`}
          </h1>
          <p className="mt-3 text-sm md:text-base text-background-200/90 max-w-2xl leading-relaxed">
            按内容分类、影视类型、地区与年份快速筛选，找到你想看的剧集与电影，并查看完整剧情介绍。
          </p>
        </div>
      </div>

      <div className="w-full px-4 md:px-8 py-8 md:py-10">
        <FilterBar groups={groups} values={values} onChange={setParam} onReset={reset} />

        <div className="mt-6 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center flex-wrap gap-2 text-sm text-foreground-600">
            <span>
              共找到 <strong className="text-accent-600">{filtered.length}</strong> 部作品
            </span>
            {activeTags.length > 0 && (
              <span className="hidden sm:flex items-center gap-1.5">
                {activeTags.map((t) => (
                  <span key={t} className="px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-900 text-xs">
                    {t}
                  </span>
                ))}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1 p-1 rounded-full bg-background-100 w-fit">
            <button
              type="button"
              onClick={() => setParam("sort", "update")}
              className={`whitespace-nowrap px-3 py-1 rounded-full text-sm transition-colors cursor-pointer ${
                sort === "update" ? "bg-accent-500 text-primary-950 font-semibold" : "text-foreground-600"
              }`}
            >
              按更新时间
            </button>
            <button
              type="button"
              onClick={() => setParam("sort", "rating")}
              className={`whitespace-nowrap px-3 py-1 rounded-full text-sm transition-colors cursor-pointer ${
                sort === "rating" ? "bg-accent-500 text-primary-950 font-semibold" : "text-foreground-600"
              }`}
            >
              按热度
            </button>
          </div>
        </div>

        {pageItems.length > 0 ? (
          <>
            <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
              {pageItems.map((item) => (
                <MovieCard key={item.id} item={item} />
              ))}
            </div>
            <Pagination
              current={safePage}
              total={totalPages}
              onChange={(p) => {
                const next = new URLSearchParams(params);
                if (p === 1) next.delete("page");
                else next.set("page", String(p));
                setParams(next, { replace: true });
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
            />
          </>
        ) : (
          <div className="mt-10 flex flex-col items-center justify-center py-16 rounded-lg bg-background-100 border border-background-200">
            <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-200 text-foreground-400">
              <i className="ri-inbox-2-line text-3xl"></i>
            </span>
            <p className="mt-4 font-heading text-lg font-semibold text-foreground-800">暂无符合条件的影视作品</p>
            <p className="mt-1.5 text-sm text-foreground-500">试试更换或减少筛选条件</p>
            <button
              type="button"
              onClick={reset}
              className="mt-5 whitespace-nowrap px-5 py-2 rounded-md bg-accent-500 text-primary-950 text-sm font-semibold hover:bg-accent-400 transition-colors cursor-pointer"
            >
              重置筛选
            </button>
          </div>
        )}
      </div>
    </div>
  );
}